import qs from 'qs';
import sureAxios from '../services/sureAxios';
import get from 'lodash/get';
import {
  sanitizeAddress,
  sanitizeHolders,
  sanitizeBeneficiaries,
} from './utils';
import { destructureAxiosReq } from '../services/utils';

/**
 * Create a new quote.
 *
 * @param {Object} opts The options.
 * @param {Object} opts.broker The broker.
 * @param {String} opts.broker.id The broker id.
 * @param {Object} opts.client The client details.
 * @param {String} opts.client.id The client id.
 * @param {String} opts.verticalCode The vertical code.
 */
export function createQuote(opts = {}) {
  const payload = {
    broker: opts.broker,
    client: opts.client
      ? {
          id: opts.client.id,
        }
      : undefined,
    vertical_code: opts.verticalCode,
    application: {
      ...opts.application,
      rating_address: opts.client
        ? {
            region: get(opts, 'client.person.address.region'),
          }
        : get(opts, 'application.rating_address'),
    },
  };
  return destructureAxiosReq(sureAxios.post(`/api/quotes/v1/quotes`, payload));
}

/**
 * Update a Passport 360 quote.
 *
 * @param {Object} opts The options.
 * @param {Object} opts.quote The updated quote object.
 */
export function updatePassport360Quote({ quote }) {
  const application = quote.application;
  const payload = {
    ...quote,
    application: {
      holders: sanitizeHolders(application.holders),
      beneficiaries: sanitizeBeneficiaries(application.beneficiaries),
      client: {
        first_name: application.client.first_name,
        last_name: application.client.last_name,
        email: application.client.email,
        phone_number: application.client.phone_number,
        address: sanitizeAddress(application.client.address),
        company_name: application.client.company_name,
      },
      rating_address: sanitizeAddress(application.holders[0].address),
      billing: application.billing,
      auto_renewal: application.auto_renewal,
      plan_id: application.plan_id,
      policy_effective_date: application.policy_effective_date,
    },
  };
  return destructureAxiosReq(
    sureAxios.put(`/api/quotes/v1/quotes/${quote.quote_number}`, payload)
  );
}

/**
 * Update a BOA quote.
 *
 * @param {Object} opts The options.
 * @param {Object} opts.quote The updated quote object.
 */
export function updateBOAQuote({ quote }) {
  const application = quote.application;
  const payload = {
    ...quote,
    application: {
      holders: sanitizeHolders(application.holders),
      beneficiaries: sanitizeBeneficiaries(application.beneficiaries),
      client: {
        first_name: application.client.first_name,
        last_name: application.client.last_name,
        email: application.client.email,
        phone_number: application.client.phone_number,
        address: sanitizeAddress(application.client.address),
        company_name: application.client.company_name,
        client_type: application.client.client_type,
      },
      rating_address: sanitizeAddress(application.rating_address),
      billing: application.billing,
      auto_renewal: application.auto_renewal,
      plan_id: application.plan_id,
      policy_effective_date: application.policy_effective_date,
      answers: application.answers,
    },
  };
  return destructureAxiosReq(
    sureAxios.put(`/api/quotes/v1/quotes/${quote.quote_number}`, payload)
  );
}

/**
 * Handoff a quote.
 *
 * @param {Object} opts The options.
 * @param {String} opts.quoteNumber The quote number.
 * @param {String} opts.type Handoff type: "proposal" or "blank".
 */
export function handoffQuote(opts = {}) {
  const payload = {
    handoff_type: opts.handoffType,
  };
  return destructureAxiosReq(
    sureAxios.patch(`api/quotes/v1/quotes/${opts.quoteNumber}/handoff`, payload)
  );
}

/**
 * Get a single quote.
 *
 * @param {Object} opts The options.
 * @param {String} opts.quoteId The quote id to fetch.
 */
export function getQuote({ quoteId }) {
  return destructureAxiosReq(sureAxios.get(`/api/quotes/v1/quotes/${quoteId}`));
}

/**
 * Get a list of quotes.
 *
 * @param {Object} opts The options.
 * @param {String} opts.authToken The auth token.
 * @param {String} [opts.clientId] The client id to filter by.
 */
export function getQuotes(opts = {}) {
  const payload = {
    client_id: opts.clientId,
  };
  return destructureAxiosReq(
    sureAxios.get(`/api/quotes/v1/quotes?${qs.stringify(payload)}`)
  );
}

/**
 * Get plans for a quote.
 *
 * @param {Object} opts The options.
 * @param {String} opts.quoteId The quote id.
 */
export function getPlans({ quoteId }) {
  return destructureAxiosReq(
    sureAxios.get(`/api/quotes/v1/quotes/${quoteId}/plans`)
  );
}

/**
 * Checkout a quote.
 *
 * @param {Object} opts The options.
 * @param {String} opts.quoteId The quote id.
 */
export function checkoutQuote({ quoteId }) {
  return destructureAxiosReq(
    sureAxios.get(`/api/quotes/v1/quotes/${quoteId}/checkout`)
  );
}

/**
 * Finalize a quote.
 *
 * @param {Object} opts The options.
 * @param {String} opts.quoteId The quote id.
 */
export function finalizeQuote({ quoteId, deleteAuthHeader }) {
  if (deleteAuthHeader) {
    delete sureAxios.defaults.headers['Authorization'];
  }
  return destructureAxiosReq(
    sureAxios.post(`/api/quotes/v1/quotes/${quoteId}/finalize`, {})
  );
}
